import React, { useState, useEffect, useMemo, useCallback } from 'react';
import componentStyles from './betCart.module.css';
import globalStyles from '../globals.module.css';
import BetCartService from '../../service/betCartService';
import { generatePropsViewerBetLink } from '../../service/propsViewerService';
import { getSnipedTicketOptions } from '../../service/snipedOddsService';
import eventBus from '../../service/eventBus';
import { EVENT_TRIGGER_BET_CART, EVENT_BET_CART_UPDATED } from '../../utils/constants';
import { FaTimes } from 'react-icons/fa';
import AnalyticsService from '../../service/analyticsService';
import SmallLoadingAlt from '../global/smallLoadingAlt';
import {
    EVENT_BET_CART_OPENED,
    EVENT_BET_CART_UPDATED as ANALYTICS_BET_CART_UPDATED,
    EVENT_BETSLIP_GENERATED
} from '../../utils/analyticsConstants';
import sportsbookBetLinkOptions from '../../config/sportsbookBetLinkOptions';

const confidenceRank = {
    validated: 3,
    inferred: 2,
    unvalidated: 1
};

const SNIPED_SOURCE = 'sniped_odds';

const isSnipedBet = (bet) => bet?.source === SNIPED_SOURCE || Boolean(bet?.statspyderDocumentId);

const getSnipedDocumentId = (bet) => (
    bet?.statspyderDocumentId ||
    bet?.sourcePayload?.statspyder_document_id ||
    bet?.sourceBetId ||
    bet?.cartKey ||
    ''
);

const getSnipedOptionKey = (option, index) => [
    option?.bookmakerKey || option?.bookmaker || 'book',
    option?.marketKey || option?.finalLeg?.marketType || 'market',
    option?.line ?? option?.finalLeg?.line ?? 'line',
    option?.odds ?? 'odds',
    index
].join('|');

const normalizeBookmakerKey = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const optionMatchesBookmaker = (option, bookmakerOption) => {
    if (!option || !bookmakerOption) return false;

    const optionKeys = [
        option.bookmakerKey,
        option.bookmaker,
        option.finalLeg?.bookmakerKey
    ].map(normalizeBookmakerKey).filter(Boolean);
    const targetKeys = [
        bookmakerOption.bookmakerKey,
        bookmakerOption.displayName,
        ...(bookmakerOption.aliases || [])
    ].map(normalizeBookmakerKey).filter(Boolean);

    return optionKeys.some((key) => targetKeys.includes(key));
};

const formatSnipedLine = (option) => {
    const line = option?.line ?? option?.finalLeg?.line ?? 'N/A';
    const odds = option?.odds ?? 'N/A';
    return [line, odds !== 'N/A' ? `(${odds})` : ''].filter(Boolean).join(' ');
};

const getDefaultSnipedOptionKey = (options = []) => {
    if (!Array.isArray(options) || options.length === 0) return '';
    const preferredIndex = options.findIndex((option) => option.status === 'direct_link' && option.isExactLine);
    const directIndex = options.findIndex((option) => option.status === 'direct_link');
    const exactIndex = options.findIndex((option) => option.isExactLine);
    let index = 0;

    if (preferredIndex >= 0) {
        index = preferredIndex;
    } else if (directIndex >= 0) {
        index = directIndex;
    } else if (exactIndex >= 0) {
        index = exactIndex;
    }

    return getSnipedOptionKey(options[index], index);
};

const getSelectedSnipedOption = (ticketItem, selectedKey) => {
    const options = Array.isArray(ticketItem?.options) ? ticketItem.options : [];
    if (options.length === 0) return null;
    const key = selectedKey || getDefaultSnipedOptionKey(options);
    return options.find((option, index) => getSnipedOptionKey(option, index) === key) || options[0];
};

const getSelectedSnipedOptionKey = (options = [], selectedKey = '') => {
    if (!Array.isArray(options) || options.length === 0) return '';
    const selectedKeyIsAvailable = options.some((option, index) => (
        getSnipedOptionKey(option, index) === selectedKey
    ));
    return selectedKeyIsAvailable ? selectedKey : getDefaultSnipedOptionKey(options);
};

const buildBetLinkLeg = (bet) => {
    const source = bet.betLinkLeg || bet;

    return {
        id: source.sourceBetId || bet.sourceBetId || source.id,
        betType: source.betType || bet.betType || String(source.type || bet.type || '').toLowerCase(),
        label: source.label || bet.label || source.bet || bet.bet,
        sport: source.sport || bet.sport,
        eventId: source.eventId || bet.eventId || source.oddsApiEventId || bet.oddsApiEventId || source.scoutingReportId || bet.scoutingReportId,
        marketType: source.marketType || bet.marketType,
        side: source.side || bet.side,
        player: source.player || bet.player,
        direction: source.direction || bet.direction,
        line: source.line ?? bet.line,
        recommendedBet: source.recommendedBet || bet.recommendedBet || source.bet || bet.bet,
        confidence: source.confidence || bet.confidence,
        marketDisplayName: source.marketDisplayName || bet.marketDisplayName
    };
};

const isValidBetLinkLeg = (leg) => {
    if (!leg || !leg.betType || !leg.sport || !leg.eventId || !leg.recommendedBet) {
        return false;
    }

    if (leg.betType === 'prop') {
        return Boolean(
            leg.player &&
            leg.marketType &&
            leg.direction &&
            leg.line !== null &&
            leg.line !== undefined
        );
    }

    if (leg.betType === 'moneyline') {
        return Boolean(leg.side);
    }

    if (leg.betType === 'spread') {
        return Boolean(leg.side && leg.line !== null && leg.line !== undefined);
    }

    if (leg.betType === 'total') {
        return Boolean(leg.direction && leg.line !== null && leg.line !== undefined);
    }

    return false;
};

/**
 * BetCart - Modal component to display and manage saved bets
 */
const BetCart = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [bets, setBets] = useState([]);
    const [selectedBookmakerKey, setSelectedBookmakerKey] = useState('');
    const [selectedState, setSelectedState] = useState('');
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);
    const [betLinkError, setBetLinkError] = useState('');
    const [betLinkResult, setBetLinkResult] = useState(null);
    const [snipedTicketOptionsByBetId, setSnipedTicketOptionsByBetId] = useState({});
    const [selectedSnipedOptionKeys, setSelectedSnipedOptionKeys] = useState({});
    const [isLoadingSnipedOptions, setIsLoadingSnipedOptions] = useState(false);
    const [snipedOptionsError, setSnipedOptionsError] = useState('');
    const betCartService = useMemo(() => BetCartService.getInstance(), []);
    const excludeDfs = sportsbookBetLinkOptions.defaultExcludeDfs !== false;
    const allowClosestLine = sportsbookBetLinkOptions.defaultAllowClosestLine !== false;
    const snipedBets = useMemo(() => bets.filter(isSnipedBet), [bets]);
    const regularBets = useMemo(() => bets.filter((bet) => !isSnipedBet(bet)), [bets]);
    const hasSnipedBets = snipedBets.length > 0;
    const sportsbookOptions = useMemo(() => (
        sportsbookBetLinkOptions.options
            .filter((option) => !excludeDfs || option.category !== 'dfs')
            .filter((option) => bets.length <= 1 || option.multiPickSupported)
            .sort((a, b) => {
                if (a.uiRecommended !== b.uiRecommended) return a.uiRecommended ? -1 : 1;
                if (a.multiPickSupported !== b.multiPickSupported) return a.multiPickSupported ? -1 : 1;
                const confidenceDelta = (confidenceRank[b.multiPickConfidence] || 0) - (confidenceRank[a.multiPickConfidence] || 0);
                if (confidenceDelta !== 0) return confidenceDelta;
                return a.displayName.localeCompare(b.displayName);
            })
    ), [bets.length, excludeDfs]);
    const snipedAvailableBookKeys = useMemo(() => {
        const keys = new Set();
        Object.values(snipedTicketOptionsByBetId).forEach((item) => {
            (item?.options || []).forEach((option) => {
                const key = option.bookmakerKey || option.finalLeg?.bookmakerKey || '';
                if (key) keys.add(normalizeBookmakerKey(key));
            });
        });
        return keys;
    }, [snipedTicketOptionsByBetId]);
    const visibleSportsbookOptions = useMemo(() => {
        if (!hasSnipedBets || snipedAvailableBookKeys.size === 0) {
            return sportsbookOptions;
        }

        return sportsbookOptions.filter((option) => (
            snipedAvailableBookKeys.has(normalizeBookmakerKey(option.bookmakerKey)) ||
            snipedAvailableBookKeys.has(normalizeBookmakerKey(option.displayName)) ||
            (option.aliases || []).some((alias) => snipedAvailableBookKeys.has(normalizeBookmakerKey(alias)))
        ));
    }, [hasSnipedBets, snipedAvailableBookKeys, sportsbookOptions]);
    const selectedBookmaker = visibleSportsbookOptions.find((option) => option.bookmakerKey === selectedBookmakerKey);

    const loadBets = useCallback(() => {
        const nextBets = betCartService.getAllBets();
        setBets((prevBets) => (
            JSON.stringify(prevBets) === JSON.stringify(nextBets) ? prevBets : nextBets
        ));
    }, [betCartService]);

    const loadSnipedTicketOptions = useCallback(async () => {
        if (!isOpen || snipedBets.length === 0) {
            setSnipedTicketOptionsByBetId({});
            setSelectedSnipedOptionKeys({});
            setSnipedOptionsError('');
            return;
        }

        setIsLoadingSnipedOptions(true);
        setSnipedOptionsError('');

        try {
            const response = await getSnipedTicketOptions({
                items: snipedBets.map((bet) => {
                    const statspyderDocumentId = getSnipedDocumentId(bet);
                    return statspyderDocumentId
                        ? { statspyderDocumentId }
                        : { sourcePayload: bet.sourcePayload || bet };
                }),
                state: selectedState.trim().toLowerCase(),
                allowClosestLine: true,
                excludeDfs: false
            });
            const responseItems = response?.data?.results?.items || [];
            const nextOptionsByBetId = {};

            responseItems.forEach((item, index) => {
                const responseDocumentId = item?.sniped?.statspyderDocumentId || item?.sniped?.statspyder_document_id || '';
                const matchingBet = snipedBets.find((bet) => getSnipedDocumentId(bet) === responseDocumentId) || snipedBets[index];
                if (!matchingBet?.id) return;

                nextOptionsByBetId[matchingBet.id] = item;
            });

            setSnipedTicketOptionsByBetId(nextOptionsByBetId);
            setSelectedSnipedOptionKeys((prev) => {
                const nextSelectedKeys = {};
                Object.entries(nextOptionsByBetId).forEach(([betId, item]) => {
                    const currentKey = prev[betId];
                    const options = Array.isArray(item?.options) ? item.options : [];
                    const currentStillAvailable = options.some((option, optionIndex) => (
                        getSnipedOptionKey(option, optionIndex) === currentKey
                    ));
                    nextSelectedKeys[betId] = currentStillAvailable
                        ? currentKey
                        : getDefaultSnipedOptionKey(options);
                });
                return nextSelectedKeys;
            });
        } catch (error) {
            setSnipedTicketOptionsByBetId({});
            setSelectedSnipedOptionKeys({});
            setSnipedOptionsError('Unable to load sportsbook options for saved picks.');
        } finally {
            setIsLoadingSnipedOptions(false);
        }
    }, [isOpen, selectedState, snipedBets]);

    useEffect(() => {
        // Listen for cart open/close events
        const handleCartToggle = (open) => {
            if (open) {
                setIsOpen(true);
                setIsClosing(false);
                AnalyticsService.getInstance().track(EVENT_BET_CART_OPENED, { betCount: betCartService.getBetCount() });
                loadBets();
            } else {
                setIsClosing(true);
                setTimeout(() => {
                    setIsOpen(false);
                    setIsClosing(false);
                }, 300);
            }
        };

        eventBus.on('BetCart', EVENT_TRIGGER_BET_CART, handleCartToggle);

        return () => {
            eventBus.off('BetCart', EVENT_TRIGGER_BET_CART);
        };
    }, [betCartService, loadBets]);

    // Track bet cart updates (add, remove, clear) for analytics
    useEffect(() => {
        const handleCartUpdated = (payload) => {
            const props = { action: payload?.action ?? '' };
            if (payload?.betId) props.betId = payload.betId;
            if (payload?.betIds && payload.betIds.length) props.betIds = payload.betIds;
            AnalyticsService.getInstance().track(ANALYTICS_BET_CART_UPDATED, props);
        };
        eventBus.on('BetCart', EVENT_BET_CART_UPDATED, handleCartUpdated);
        return () => eventBus.off('BetCart', EVENT_BET_CART_UPDATED);
    }, []);

    // Periodically update bets when cart is open
    useEffect(() => {
        if (isOpen) {
            const interval = setInterval(() => {
                loadBets();
            }, 500);
            return () => clearInterval(interval);
        }
    }, [isOpen, loadBets]);

    useEffect(() => {
        loadSnipedTicketOptions();
    }, [loadSnipedTicketOptions]);

    useEffect(() => {
        if (visibleSportsbookOptions.length === 0) {
            setSelectedBookmakerKey('');
            return;
        }

        if (!visibleSportsbookOptions.some((option) => option.bookmakerKey === selectedBookmakerKey)) {
            setSelectedBookmakerKey(visibleSportsbookOptions[0].bookmakerKey);
        }
    }, [visibleSportsbookOptions, selectedBookmakerKey]);

    const handleClose = useCallback(() => {
        if (!isOpen || isClosing) return;
        setIsClosing(true);
        setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
        }, 300);
    }, [isOpen, isClosing]);

    const handleCloseImmediate = () => {
        setIsOpen(false);
        setIsClosing(false);
    };

    const handleRemoveBet = (betId) => {
        betCartService.removeBet(betId);
        loadBets();
    };

    const handleClearAll = () => {
        betCartService.clearCart();
        loadBets();
        setBetLinkResult(null);
        setBetLinkError('');
        handleCloseImmediate();
    };

    const getBetLinkLegs = () => {
        return bets
            .map(buildBetLinkLeg)
            .filter(isValidBetLinkLeg);
    };

    const getSnipedOptionsForSelectedBook = (bet) => {
        const ticketItem = snipedTicketOptionsByBetId[bet.id];
        const options = Array.isArray(ticketItem?.options) ? ticketItem.options : [];
        return selectedBookmaker
            ? options.filter((option) => optionMatchesBookmaker(option, selectedBookmaker))
            : [];
    };

    const handleGenerateBetSlip = async () => {
        const hasSnipedBets = snipedBets.length > 0;
        const hasRegularBets = regularBets.length > 0;
        const legs = hasSnipedBets
            ? snipedBets.map((bet) => {
                const selectedBookOptions = getSnipedOptionsForSelectedBook(bet);
                return getSelectedSnipedOption({ options: selectedBookOptions }, selectedSnipedOptionKeys[bet.id])?.finalLeg || null;
            }).filter(Boolean)
            : getBetLinkLegs();
        setBetLinkError('');
        setBetLinkResult(null);

        if (bets.length === 0) {
            setBetLinkError('Add at least one pick before generating a bet slip.');
            return;
        }

        if (hasSnipedBets && hasRegularBets) {
            setBetLinkError('Generate sniped odds and regular picks separately for now.');
            return;
        }

        if (hasSnipedBets && isLoadingSnipedOptions) {
            setBetLinkError('Sportsbook options are still loading.');
            return;
        }

        if (legs.length !== bets.length) {
            setBetLinkError('One or more picks are missing link details.');
            return;
        }

        if (hasSnipedBets && !selectedBookmakerKey) {
            setBetLinkError('Choose a sportsbook.');
            return;
        }

        if (!hasSnipedBets && !selectedBookmakerKey) {
            setBetLinkError('Choose a sportsbook.');
            return;
        }

        if (!hasSnipedBets && selectedBookmaker?.requiresState && !selectedState.trim()) {
            setBetLinkError('Enter a state for this sportsbook.');
            return;
        }

        setIsGeneratingLink(true);

        try {
            const response = await generatePropsViewerBetLink({
                source: hasSnipedBets ? SNIPED_SOURCE : 'manual_selection',
                sourcePayload: hasSnipedBets ? {
                    statspyderDocumentIds: snipedBets.map(getSnipedDocumentId).filter(Boolean),
                    workflow: 'sniped_ticket_options'
                } : undefined,
                bookmakerKey: selectedBookmakerKey,
                state: hasSnipedBets ? selectedState.trim().toLowerCase() : (selectedBookmaker?.requiresState ? selectedState.trim().toLowerCase() : ''),
                allowClosestLine: hasSnipedBets ? false : allowClosestLine,
                excludeDfs: hasSnipedBets ? false : excludeDfs,
                includeDetails: hasSnipedBets,
                persist: true,
                legs
            });
            const results = response?.data?.results || {};
            setBetLinkResult(results);
            if (response?.data?.success !== false) {
                AnalyticsService.getInstance().track(EVENT_BETSLIP_GENERATED, {
                    source: hasSnipedBets ? SNIPED_SOURCE : 'manual_selection',
                    bookmakerKey: selectedBookmakerKey,
                    legCount: legs.length,
                    betCount: bets.length,
                    betSlipId: results.betSlipId || '',
                    hasUrl: Boolean(results.url),
                    fallbackCount: Array.isArray(results.fallbackUrls) ? results.fallbackUrls.length : 0,
                    unresolvedCount: Array.isArray(results.unresolved) ? results.unresolved.length : 0,
                    warningCount: Array.isArray(results.warnings) ? results.warnings.length : 0
                });
            }
        } catch (error) {
            setBetLinkError('Unable to generate a bet slip link.');
        } finally {
            setIsGeneratingLink(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className={`${componentStyles.betCartOverlay} ${isClosing ? componentStyles.betCartOverlayClosing : componentStyles.betCartOverlayOpening}`} onClick={handleClose}>
            <div className={`${componentStyles.betCartModal} ${isClosing ? componentStyles.betCartModalClosing : componentStyles.betCartModalOpening}`} onClick={(e) => e.stopPropagation()}>
                <div className={componentStyles.betCartHeader}>
                    <h2 className={componentStyles.betCartTitle}>Saved Bets ({bets.length})</h2>
                    <button type="button" className={`${globalStyles.iconButton} ${globalStyles.squareButton}`} onClick={handleClose} aria-label="Close bet cart">
                        <FaTimes />
                    </button>
                </div>

                <div className={componentStyles.betCartBody}>
                    {bets.length === 0 ? (
                        <div className={componentStyles.emptyState}>
                            <p>No saved bets. Add bets from scouting reports to get started.</p>
                        </div>
                    ) : (
                        <>
                            <div className={componentStyles.betCartActions}>
                                <div className={componentStyles.sportsbookPicker}>
                                    <div className={componentStyles.sportsbookOptions}>
                                        {visibleSportsbookOptions.map((option) => (
                                            <button
                                                key={option.bookmakerKey}
                                                type="button"
                                                className={`${componentStyles.sportsbookOption} ${selectedBookmakerKey === option.bookmakerKey ? componentStyles.sportsbookOptionActive : ''}`}
                                                onClick={() => {
                                                    setSelectedBookmakerKey(option.bookmakerKey);
                                                    setSelectedSnipedOptionKeys({});
                                                    setBetLinkResult(null);
                                                    setBetLinkError('');
                                                }}
                                            >
                                                {option.displayName}
                                            </button>
                                        ))}
                                    </div>
                                    {selectedBookmaker?.requiresState && (
                                        <input
                                            className={componentStyles.stateInput}
                                            type="text"
                                            value={selectedState}
                                            onChange={(event) => {
                                                setSelectedState(event.target.value);
                                                setBetLinkResult(null);
                                                setBetLinkError('');
                                            }}
                                            placeholder="State, e.g. ny"
                                            maxLength={2}
                                        />
                                    )}
                                </div>
                                <button
                                    className={globalStyles.primaryButton}
                                    onClick={handleGenerateBetSlip}
                                    disabled={isGeneratingLink || isLoadingSnipedOptions || bets.length === 0}
                                >
                                    {isGeneratingLink ? 'Generating...' : isLoadingSnipedOptions ? 'Loading Options...' : 'Generate Slip'}
                                </button>
                                <button 
                                    className={globalStyles.secondaryButton}
                                    onClick={handleClearAll}
                                >
                                    Clear All
                                </button>
                            </div>
                            {(betLinkError || betLinkResult) && (
                                <div className={componentStyles.betLinkResult}>
                                    {betLinkError && <p className={componentStyles.betLinkError}>{betLinkError}</p>}
                                    {Array.isArray(betLinkResult?.warnings) && betLinkResult.warnings.length > 0 && (
                                        <ul className={componentStyles.betLinkWarnings}>
                                            {betLinkResult.warnings.map((item, index) => (
                                                <li key={`${item}-${index}`}>{item}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {betLinkResult?.url && (
                                        <a
                                            className={componentStyles.openSlipLink}
                                            href={betLinkResult.url}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Open Bet Slip
                                        </a>
                                    )}
                                    {betLinkResult?.message && <p>{betLinkResult.message}</p>}
                                    {Array.isArray(betLinkResult?.unresolved) && betLinkResult.unresolved.length > 0 && (
                                        <ul>
                                            {betLinkResult.unresolved.map((item, index) => (
                                                <li key={`${item}-${index}`}>{item}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {Array.isArray(betLinkResult?.fallbackUrls) && betLinkResult.fallbackUrls.length > 0 && (
                                        <div className={componentStyles.fallbackLinks}>
                                            {betLinkResult.fallbackUrls.map((fallback, index) => {
                                                const url = typeof fallback === 'string' ? fallback : fallback.url;
                                                const label = typeof fallback === 'string' ? `Pick ${index + 1}` : fallback.label || fallback.bookmakerKey || `Pick ${index + 1}`;

                                                if (!url) return null;

                                                return (
                                                    <a key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer">
                                                        {label}
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                            {snipedOptionsError && (
                                <div className={componentStyles.betLinkResult}>
                                    <p className={componentStyles.betLinkError}>{snipedOptionsError}</p>
                                </div>
                            )}
                            {hasSnipedBets && isLoadingSnipedOptions && (
                                <div className={componentStyles.ticketOptionsLoading}>
                                    <SmallLoadingAlt />
                                </div>
                            )}
                            {(!hasSnipedBets || !isLoadingSnipedOptions) && (
                                <div className={componentStyles.betsList}>
                                    {bets.map((bet) => {
                                    const snipedTicketItem = snipedTicketOptionsByBetId[bet.id];
                                    const snipedOptions = getSnipedOptionsForSelectedBook(bet);
                                    const isSniped = isSnipedBet(bet);
                                    const selectedBookLabel = selectedBookmaker?.displayName || 'selected sportsbook';
                                    const selectedSnipedOptionKey = getSelectedSnipedOptionKey(
                                        snipedOptions,
                                        selectedSnipedOptionKeys[bet.id]
                                    );

                                    return (
                                    <div key={bet.id} className={componentStyles.betCard}>
                                        <div className={componentStyles.betCardHeader}>
                                            <div className={componentStyles.betCardHeaderLeft}>
                                                <span className={componentStyles.betType}>{bet.type}</span>
                                                <span className={componentStyles.betSport}>{bet.sport}</span>
                                            </div>
                                            <button
                                                className={`${globalStyles.iconButton} ${globalStyles.squareButton} ${globalStyles.compactButton}`}
                                                onClick={() => handleRemoveBet(bet.id)}
                                                title="Remove bet"
                                            >
                                                <FaTimes />
                                            </button>
                                        </div>
                                        <div className={componentStyles.betCardBody}>
                                            {(bet.player || bet.marketDisplayName || bet.marketType) && (
                                                <div className={componentStyles.betContext}>
                                                    {(bet.player || bet.side) && <span>{bet.player || bet.side}</span>}
                                                    {(bet.marketDisplayName || bet.marketType) && (
                                                        <span>{bet.marketDisplayName || bet.marketType}</span>
                                                    )}
                                                </div>
                                            )}
                                            <div className={componentStyles.betRecommendation}>
                                                {bet.bet}
                                            </div>
                                            {isSniped && !isLoadingSnipedOptions && (
                                                <div className={componentStyles.snipedOptionsPanel}>
                                                    {snipedTicketItem?.status && snipedTicketItem.status !== 'available' && (
                                                        <div className={componentStyles.snipedOptionsUnavailable}>
                                                            {snipedTicketItem.message || `Ticket options ${snipedTicketItem.status}.`}
                                                        </div>
                                                    )}
                                                    {snipedTicketItem?.status === 'available' && snipedOptions.length === 0 && (
                                                        <div className={componentStyles.snipedOptionsUnavailable}>
                                                            No {selectedBookLabel} line is available for this pick.
                                                        </div>
                                                    )}
                                                    {snipedOptions.length > 1 && (
                                                        <label className={componentStyles.snipedOptionSelectWrap}>
                                                            <span>{selectedBookLabel}</span>
                                                            <select
                                                                className={componentStyles.snipedOptionSelect}
                                                                value={selectedSnipedOptionKey}
                                                                onChange={(event) => {
                                                                    setSelectedSnipedOptionKeys((prev) => ({
                                                                        ...prev,
                                                                        [bet.id]: event.target.value
                                                                    }));
                                                                    setBetLinkResult(null);
                                                                    setBetLinkError('');
                                                                }}
                                                            >
                                                                {snipedOptions.map((option, optionIndex) => {
                                                                    const optionKey = getSnipedOptionKey(option, optionIndex);
                                                                    return (
                                                                        <option key={optionKey} value={optionKey}>
                                                                            {formatSnipedLine(option)}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                        </label>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BetCart;
